import React from 'react';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ConnectionStatus, InstrumentServiceDetails} from 'instruments/connections/ConnectionConstants';
import * as InstrumentDataDAO from 'instruments/instrument-data/InstrumentDataDAO';
import InstrumentLoginPageModal from 'instruments/instrument-data/InstrumentLoginPageModal';
import {AutomationInputFileResponse} from 'lab-automation/automation-files/AutomationFileDAO';
import {runAutomationInputFileStatusModal} from 'lab-automation/automation-files/AutomationFileStatusModal';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import notifications from 'notifications';
import Button, {ButtonProps, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import {Optional} from 'util/Types';

import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import waitForEntrySaved from '../utils/waitForEntrySaved';
import {generateAutomationInputFile, setAutomationFileStatus} from './AutomationFileActions';

interface GenerateAutomationInputFileButtonProps {
  runId: string | null;
  automationInputFile: AutomationInputFileGraphQL;
  isStale: boolean;
  disabled: boolean;
  hasOutputFileConfig: boolean;
  isConnectionRun: boolean;
  connectionId: string | null;
  instrumentServiceDetails?: InstrumentServiceDetails | null;
  inputFileStatus: AutomationFileStatusGraphQL | null;
  handleRefreshVersionedData: () => Promise<void>;
  updateAutomationFiles: (
    automationInputFile: AutomationInputFileGraphQL,
    automationOutputFile: AutomationOutputFileGraphQL | null
  ) => Promise<void>;
}

const GenerateAutomationInputFileButton: React.FunctionComponent<GenerateAutomationInputFileButtonProps> = (
  props: GenerateAutomationInputFileButtonProps
): JSX.Element => {
  const editorPropertiesContext = EditorPropertiesContext.useContext();

  const setStatusToRunning = (): void => {
    setAutomationFileStatus(props.automationInputFile.id, AutomationFileStatusGraphQL.running);
  };
  const setStatusToPending = (): void => {
    setAutomationFileStatus(props.automationInputFile.id, AutomationFileStatusGraphQL.pending);
  };
  const isRunning = props.inputFileStatus === AutomationFileStatusGraphQL.running;
  const generateFile = async (
    fileId: Optional<string>,
    copyToOutputFile: boolean
  ): Promise<AutomationInputFileResponse | undefined> => {
    setStatusToRunning();
    try {
      const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
      if (saved) {
        const response = await generateAutomationInputFile(
          props.runId!,
          props.automationInputFile.automationInputFileConfig.name,
          copyToOutputFile
        );
        await props.updateAutomationFiles(
          response.automationInputFile,
          response.automationOutputFile ?? null
        );
        if (response.errorMessage) {
          notifications.error(response.errorMessage, {duration: 20});
        }
        return response;
      }
    } catch (err) {
      setStatusToPending();
      throw err;
    }
    return undefined;
  };

  const dataTestAttrs = {component: 'GenerateAutomationInputFileButton', element: 'generateButton'};
  const isConnectionImportButton = props.isConnectionRun;
  const icon = isRunning ? <BenchlingIcon icon="refresh" className="glyphicon-spin" /> : 'refresh';

  const sharedButtonProps: ButtonProps = {
    autoHeight: true,
    className: 'processAutomationFileButton',
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
    size: ButtonSize.MINI,
    fullWidth: true,
    disabled:
      props.disabled ||
      (isConnectionImportButton &&
        !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})),
    dataTestAttrs,
    icon,
  };

  // TODO(BNCH-99934): update this to no longer use the DAO
  // instead, we should update the capillary API and pass the username through to it
  const generateAndSendFile = async (username: string | null): Promise<void> => {
    const generateFileResponse = await generateFile(editorPropertiesContext.fileId, false);
    if (generateFileResponse?.automationInputFile.id) {
      await InstrumentDataDAO.createSendInputFileInstrumentQuery(
        generateFileResponse.automationInputFile.id,
        props.connectionId!,
        username
      );
    }
  };

  const runInstrumentLoginPageModal = (): void => {
    runModal(InstrumentLoginPageModal, {
      // connectionId must be set for us to have instrumentServiceDetails, controlling whether we run this modal
      connectionId: props.connectionId!,
      onRequestClose: () => {},
      onRequestSubmit: (username: string | null) => {
        generateAndSendFile(username);
      },
    });
  };

  if (props.isStale) {
    return (
      <Button
        {...sharedButtonProps}
        text={isRunning ? 'Generating - click to sync run' : 'Pending - click to sync run'}
        onClick={props.handleRefreshVersionedData}
      />
    );
  }

  if (isRunning) {
    return (
      <Button
        {...sharedButtonProps}
        text="Generating..."
        onClick={() => {
          runAutomationInputFileStatusModal({
            automationFile: props.automationInputFile,
            runId: props.runId,
            locked: props.disabled,
            editorPropertiesContext,
            updateAutomationInputFile: (updatedFile) => props.updateAutomationFiles(updatedFile, null),
          });
        }}
      />
    );
  }

  const connectionStatus = props.instrumentServiceDetails?.status.value;
  const requiresLogin = props.instrumentServiceDetails?.fields.per_user_login_required?.value === 'true';

  function onClickFunction(copyToOutputFile: boolean): void {
    if (requiresLogin) {
      runInstrumentLoginPageModal();
      // TODO(BNCH-99934): replace this logic
      // } else if (isConnectionImportButton) {
      //   generateAndSendFile(null);
    } else {
      generateFile(editorPropertiesContext.fileId, copyToOutputFile);
    }
  }

  const isConnectionAvailable = !isConnectionImportButton || connectionStatus === ConnectionStatus.AVAILABLE;
  const isButtonDisabled =
    sharedButtonProps.disabled ||
    (!BenchlingConfig.INSTRUMENTS_ENABLE_OFFLINE_CONNECTION_DEMOS && !isConnectionAvailable);

  if (!props.hasOutputFileConfig) {
    return (
      <div>
        <Button
          {...sharedButtonProps}
          text={isConnectionImportButton ? 'Generate and send to connection' : 'Generate'}
          onClick={() => {
            onClickFunction(false);
          }}
          disabled={isButtonDisabled}
        />
      </div>
    );
  }

  const generateText = isConnectionImportButton ? 'Generate and send to connection' : 'Generate input file';
  const generateToOutputText = isConnectionImportButton
    ? 'Generate and send to connection and output'
    : 'Generate and send to output';
  return (
    <Dropdown
      menu={
        <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
          <Option
            value={generateText}
            onSelect={() => {
              onClickFunction(false);
            }}
            dataTestAttrs={{
              component: 'GenerateAutomationInputFileButton',
              element: 'option',
              key: 'generate-input-file',
            }}
          >
            {generateText}
          </Option>
          <Option
            value={generateToOutputText}
            onSelect={() => {
              onClickFunction(true);
            }}
            dataTestAttrs={{
              component: 'GenerateAutomationInputFileButton',
              element: 'option',
              key: 'generate-and-send-to-output',
            }}
          >
            {generateToOutputText}
          </Option>
        </Menu>
      }
      allowMenuToCloseDropdown={false}
    >
      <Button {...sharedButtonProps} text="Generate" disabled={isButtonDisabled} />
    </Dropdown>
  );
};

export default GenerateAutomationInputFileButton;
